import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import { useAppSelector } from '@/app/hook';
import { PSButton } from '@/components/ui/button';
import { selectUseId } from '@/features/auth/auth.slice';
import { BlockType } from '@/interfaces/models/Knowledge/scenario.interface';

import AddNoteButton from '../../AddNoteButton';
import NodeParentChanger from '../../NodeParentChanger';
import NoteForm, { getNoteSchema, INoteForm } from '../../NoteForm';
import ScenarioHeader from '../../ScenarioHeader';
import { MacBotActionKind } from '../../types';
import TextScenarioDeleteButton from './scenario-delete-button';
import ScenarioSide from './scenario-side';

export const EndConversationForm = ({
  setShow,
  itemData,
  parentId,
  tree,
  loading,
  createScenarioBlock,
  deleteScenarioBlock,
  changeScenarioBlock,
}: {
  setShow: (item: boolean) => void;
  itemData: any;
  loading: boolean;
  createScenarioBlock: (block: any) => Promise<void>;
  deleteScenarioBlock: (block: any) => Promise<void>;
  changeScenarioBlock: (block: any) => Promise<void>;
  parentId: string | null;
  tree: any[];
}) => {
  const { t } = useTranslation();
  const userId = useAppSelector(selectUseId);
  const components = {
    DropdownIndicator: null,
  };

  interface IEndConversationForm extends INoteForm {
    closeChat: boolean;
    downFor?: null | number;
  }

  const schema = Yup.object({
    closeChat: Yup.boolean(),
    ...getNoteSchema(t),
  });

  const [isDelete, setIsDelete] = useState(true);
  useEffect(() => {
    if (!itemData) {
      setIsDelete(false);
    }
  }, [itemData]);

  const {
    reset,
    setValue,
    handleSubmit,
    getValues,
    watch,
    register,
    formState: { errors },
    control,
  } = useForm<IEndConversationForm>({
    defaultValues: {
      closeChat: false,
      downFor: null,
      notes: [
        {
          note_id: uuidv4(),
          creation_date_time: new Date().toISOString(),
          title: '',
          description: '',
          qc: false,
          author: userId,
        },
      ],
    },
    resetOptions: {
      keepDefaultValues: true,
    },
    resolver: yupResolver(schema) as any,
    mode: 'onSubmit',
  });
  const { fields } = useFieldArray({
    control,
    name: 'notes',
  });

  useEffect(() => {
    reset();
  }, []);

  const deleteHandler = () => {
    deleteScenarioBlock(itemData).then(res => {
      setShow(false);
    });
  };

  useEffect(() => {
    if (itemData && itemData?.closeChat) setValue('closeChat', itemData?.closeChat);
    setValue('notes', itemData?.notes);
  }, [itemData]);

  const ButtonSubmit = (data: IEndConversationForm) => {
    if (itemData) {
      const _data = {
        ...itemData,
        id: itemData.id,
        type: BlockType.EndOFConversation,
        parent: itemData.parent,
        closeChat: data.closeChat,
        downFor: data.downFor,
        notes: data.notes,
      };
      changeScenarioBlock(_data).then(() => {
        setShow(false);
      });
    } else {
      const _data = {
        type: BlockType.EndOFConversation,
        parent: parentId,
        closeChat: data.closeChat,
        downFor: data.downFor,
        title: 'End of Conversation',
        notes: data.notes,
      };
      createScenarioBlock(_data).then(() => {
        setShow(false);
      });
    }
  };

  return (
    <React.Fragment>
      <ScenarioSide onSubmit={handleSubmit(ButtonSubmit)} size="md">
        <ScenarioHeader
          type={MacBotActionKind.ShowEndOfConversationForm}
          onClose={() => {
            reset();
          }}
        />

        <div className="tw-w-full tw-p-3">
          <div className=" tw-mt-5 tw-w-full tw-h-12 tw-pl-4 tw-pr-3  tw-shadow tw-py-3 tw-bg-white tw-rounded-md tw-border tw-border-solid tw-border-gray-300 tw-justify-between tw-items-center tw-inline-flex">
            <div className="tw-text-[#11181c] tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">{t('MacEndConversation/close')}</div>
            <Form.Check
              defaultChecked={itemData?.closeChat}
              onChange={e => setValue('closeChat', e.target.checked)}
              type="switch"
              id="IsVoiceActive"
              className="tw-font-sofiaSans tw-font-medium tw-text-sm tw-flex tw-justify-start tw-items-center tw-gap-2"
            />
          </div>
          {watch('closeChat') && (
            <div className=" tw-mt-5 tw-w-full tw-h-12 tw-pl-4 tw-pr-3  tw-shadow tw-py-3 tw-bg-white tw-rounded-md tw-border tw-border-solid tw-border-gray-300 tw-justify-between tw-items-center tw-inline-flex">
              <div className="tw-text-[#11181c] tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">{t('MacEndConversation/keep-alive')}</div>
              <Form.Check
                defaultChecked={itemData?.downFor ? true : false}
                onChange={e => setValue('downFor', e.target.checked ? 10 : null)}
                type="switch"
                className="tw-font-sofiaSans tw-font-medium tw-text-sm tw-flex tw-justify-start tw-items-center tw-gap-2"
              />
            </div>
          )}
        </div>

        <div className="tw-flex flex-row tw-justify-between tw-items-center">
          <div className="tw-flex tw-justify-start tw-items-center tw-gap-2">
            <TextScenarioDeleteButton itemData={itemData} tree={tree} deleteHandler={deleteHandler} />

            {itemData?.id && <NodeParentChanger tree={tree} currentId={itemData.id} />}
            <AddNoteButton noteFormMethods={{ getValues, setValue }} />
          </div>
          <div className="tw-flex tw-justify-end">
            <PSButton id="btn-submit-end-conversation" type="submit" disabled={loading} isLoading={loading} title={t('MacBotResponseForm/btn-02')} />
          </div>
        </div>
        <NoteForm noteFormMethods={{ register, watch, errors, fields, setValue }} />
      </ScenarioSide>
    </React.Fragment>
  );
};
